import {isUndefined} from 'lodash';
import React, {FC, Ref, useCallback, useEffect, useRef, useState} from 'react';
import styled, {css} from 'styled-components';

import {
  ClickEventHandler,
  FrontClickEvent,
  MouseEventHandler,
} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {LinkedConversationStatusesEnum} from '../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {blendColors} from '../../../../libs/shared/core/src/models/helpers/colorHelpers';
import {
  CellsActionsEnum,
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {CellsContext} from '../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {BlockLink} from '../../../../libs/shared/ui/src/components/links/blockLink';
import {LegacyInteractionParent} from '../../../../libs/shared/ui/src/interactionTracking/interactionParent';
import {useAppDispatch} from '../../../helpers/appReduxHelpers';
import {CellsConversationsDragItem} from '../../../helpers/conversationCellHelpers';
import {isDemoConversation} from '../../../helpers/modelHelpers';
import {
  ConversationActionContextFinder,
  ConversationStatusesEnum,
  makeFindConversationActionContext,
  UnreadStatusesEnum,
} from '../../../helpers/models/conversationHelpers';
import {toggleMarkConversationAsSeenAction} from '../../../store/data/conversations/seenConversationsActions';
import {setConversationsStatusAction} from '../../../store/data/conversations/statusConversationsActions';
import {CellBase} from './cellBase';
import {CompactConversationCellContent} from './cellContent/compactConversationCellContent';
import {ConversationCellContent} from './cellContent/conversationCellContent';
import {CompactCellBase} from './compactCellBase';
import {useCompactP2Context} from './compactP2Context';
import {StyledCellHoverIconsWrapper} from './content/cell/cellHoverIcons';
import {StyledCellStatusIconBaseDiv} from './content/cell/cellStatusIcon';
import {ConversationCellCommonProps} from './conversationCellCommon';
import {ConversationCellDragSource} from './conversationCellDragSource';

/*
 * Props.
 */

export type ConversationCellClickEventHandler = (
  conversationId: number,
  event: FrontClickEvent<HTMLElement>,
) => void;
export type ConversationCellDoubleClickHandler = (conversationId: number) => void;

export interface ConversationCellProps extends ConversationCellCommonProps {
  sourceCustomViewId: number | undefined;
  path?: string;
  onClick?: ConversationCellClickEventHandler;
  onDoubleClick?: ConversationCellDoubleClickHandler;
  originalSeenAt?: number;
  innerRef?: Ref<HTMLDivElement>;
  isPreview?: boolean;
  isTransparentPreview?: boolean;
  mostRecentClickConversationIdRef?: React.MutableRefObject<number>;
  secondMostRecentClickConversationIdRef?: React.MutableRefObject<number>;
  areHoverActionsEnabled?: boolean;
  customDragItem?: CellsConversationsDragItem;
  isLast?: boolean;
  isFirst?: boolean;
}

/*
 * Style.
 */

interface StyledCellBaseProps {
  $shouldShowQuickActionsOnCellHover: boolean;
  $shouldShowQuickActions: boolean;
  $isCompact: boolean;
}

const StyledCellBase = styled(CellBase)<StyledCellBaseProps>`
  ${(props) => addCellBaseHoverStyles(props)}
`;

const StyledCompactCellBase = styled(CompactCellBase)<StyledCellBaseProps>`
  ${(props) => addCellBaseHoverStyles(props)}
`;

function addCellBaseHoverStyles(props: StyledCellBaseProps) {
  if (props.$shouldShowQuickActions) {
    return css`
      ${StyledCellHoverIconsWrapper} {
        display: flex;
      }
      ${StyledCellStatusIconBaseDiv} {
        ${!props.$isCompact ? 'display: none;' : ''}
      }

      background-color: ${(p) => blendColors(p.theme.greys.shade20, p.theme.alphas.gray10)};
    `;
  }

  if (props.$shouldShowQuickActionsOnCellHover) {
    return css`
      &:hover ${StyledCellHoverIconsWrapper} {
        display: flex;
      }
      &:hover ${StyledCellStatusIconBaseDiv} {
        ${!props.$isCompact ? 'display: none;' : ''}
      }
    `;
  }

  return '';
}

/*
 * Component.
 */

export const ConversationCell: FC<ConversationCellProps> = (props) => {
  const {cellsContext, conversation, path, originalSeenAt, isPreview, isTransparentPreview} = props;
  const {id} = conversation;
  const isVisuallySelected = props.isSelected;
  const prevMouseDownTimeStampRef = useRef(0);
  const isDoubleClickDisabledRef = useRef(false);
  const {isCompact} = useCompactP2Context();

  const interactionId = `conversation-cell-${id}`;

  const {mostRecentClickConversationIdRef, secondMostRecentClickConversationIdRef} = props;

  const [isSnoozeDropdownOpen, setIsSnoozeDropdownOpen] = useState(false);

  // We hide the dropdown if the conversation gets archived and disappears.
  useEffect(() => {
    setIsSnoozeDropdownOpen(false);
  }, [id]);

  const onSnoozeDropdownClose = () => {
    setIsSnoozeDropdownOpen(false);
  };

  const onSnoozeClick: (event: FrontClickEvent) => void = (event) => {
    event.preventDefault();
    setIsSnoozeDropdownOpen(true);
  };

  const onClick: ClickEventHandler = (event) => {
    if (!props.onClick) {
      return;
    }

    if (isDemoConversation(conversation)) {
      trackInterfaceEvent(InterfaceEventsEnum.P2_DEMO_CELL_OPEN, {
        subject: conversation.subject,
      });
    }

    if (mostRecentClickConversationIdRef && secondMostRecentClickConversationIdRef) {
      secondMostRecentClickConversationIdRef.current = mostRecentClickConversationIdRef.current;
      mostRecentClickConversationIdRef.current = conversation.id;
    }

    props.onClick(id, event);
  };

  const dispatch = useAppDispatch();

  const isRead = props.unreadStatus === UnreadStatusesEnum.NONE;
  const onToggleMarkAsRead = useCallback(() => {
    // We want to send the originalSeenAt only in the case of the read -> unread transition.
    const seenAt = isRead ? originalSeenAt : undefined;

    dispatch(toggleMarkConversationAsSeenAction([id], !isRead, {cellsContext, seenAt}));
  }, [dispatch, id, isRead, cellsContext, originalSeenAt]);

  const onSetConversationsStatus = useCallback(
    (
      currentCellsContext: CellsContext,
      convoIds: readonly number[],
      newStatus: ConversationStatusesEnum,
      findConversationActionContext: ConversationActionContextFinder,
    ) => {
      dispatch(
        setConversationsStatusAction(currentCellsContext, convoIds, newStatus, findConversationActionContext),
      );
    },
    [dispatch],
  );

  const onDoubleClick: MouseEventHandler = (event) => {
    if (!props.onDoubleClick || isDoubleClickDisabledRef.current) {
      return;
    }

    if (event.defaultPrevented) {
      return;
    }

    // The two clicks are not for the same conversation ID.
    if (
      mostRecentClickConversationIdRef &&
      secondMostRecentClickConversationIdRef &&
      mostRecentClickConversationIdRef.current !== secondMostRecentClickConversationIdRef.current
    ) {
      return;
    }

    props.onDoubleClick(id);
  };

  const onMouseDown: MouseEventHandler = (event) => {
    isDoubleClickDisabledRef.current = event.timeStamp === prevMouseDownTimeStampRef.current;
    prevMouseDownTimeStampRef.current = event.timeStamp;
  };

  const {inSpam, inTrash} = cellsContext;

  const shouldShowQuickActionsOnCellHover = !inSpam && !inTrash && Boolean(props.areHoverActionsEnabled);

  const hasDoubleBottomRightIcons = props.team && props.conversation.hasAttachments;

  const onOpenClick: (event: FrontClickEvent) => void = (event) => {
    event.preventDefault();
    trackInterfaceEvent(InterfaceEventsEnum.P2_CELL_CLICK, {
      action: props.reminderDate === undefined ? CellsActionsEnum.REOPEN : CellsActionsEnum.UNSNOOZE,
    });
    onSetConversationsStatus(
      cellsContext,
      [conversation.id],
      ConversationStatusesEnum.OPEN,
      makeFindConversationActionContext(cellsContext, [conversation]),
    );
  };

  const onTrashClick: (event: FrontClickEvent) => void = (event) => {
    event.preventDefault();

    onSetConversationsStatus(
      cellsContext,
      [conversation.id],
      ConversationStatusesEnum.TRASHED,
      makeFindConversationActionContext(cellsContext, [conversation]),
    );
    trackInterfaceEvent(InterfaceEventsEnum.P2_CELL_CLICK, {
      action: CellsActionsEnum.TRASH,
    });
  };

  const isOriginalConversation = conversation.linkStatus === LinkedConversationStatusesEnum.ORIGINAL;

  const renderCell = () => {
    if (isCompact && !isPreview && !isTransparentPreview) {
      return (
        <StyledCompactCellBase
          $isCompact
          $shouldShowQuickActionsOnCellHover={shouldShowQuickActionsOnCellHover}
          $shouldShowQuickActions={isSnoozeDropdownOpen}
          isSelected={isVisuallySelected}
          isFirst={props.isFirst}
          isLast={props.isLast}
        >
          <CompactConversationCellContent
            applicationChannelType={props.applicationChannelType}
            assignee={props.assignee}
            bumpActivity={props.bumpActivity}
            bumpComment={props.bumpComment}
            bumpTeammateSource={props.bumpTeammateSource}
            cellsContext={props.cellsContext}
            commentDraftSummary={props.commentDraftSummary}
            commentingTeammate={props.commentingTeammate}
            conversation={props.conversation}
            conversationHighlight={props.conversationHighlight}
            conversationSenders={props.conversationSenders}
            date={props.date}
            draft={props.draft}
            draftSeedAuthors={props.draftSeedAuthors}
            externalChannelType={props.externalChannelType}
            hasAutoShareDrafts={props.hasAutoShareDrafts}
            inbox={props.inbox}
            isSelected={props.isSelected}
            isSnoozeDropdownOpen={isSnoozeDropdownOpen}
            isStarred={props.isStarred}
            lastComment={props.lastComment}
            lastCommentDraft={props.lastCommentDraft}
            lastManualMessage={props.lastManualMessage}
            mentionableAliases={props.mentionableAliases}
            numMessages={props.numMessages}
            onOpenClick={onOpenClick}
            onSnoozeClick={onSnoozeClick}
            onSnoozeDropdownClose={onSnoozeDropdownClose}
            onToggleMarkAsRead={onToggleMarkAsRead}
            onTrashClick={onTrashClick}
            overlay={props.overlay}
            recipient={props.recipient}
            reminderDate={props.reminderDate}
            replyingTeammate={props.replyingTeammate}
            searchActivity={props.searchActivity}
            searchResultCommentSummary={props.searchResultCommentSummary}
            shouldConsiderInPrivateTag={props.shouldConsiderInPrivateTag}
            shouldRenderConversationSenders={props.shouldRenderConversationSenders}
            shouldShowQuickActionsOnCellHover={shouldShowQuickActionsOnCellHover}
            status={props.status}
            tags={props.tags}
            team={props.team}
            trackerStatus={props.trackerStatus}
            trackerTeammates={props.trackerTeammates}
            unreadStatus={props.unreadStatus}
            userId={props.userId}
            isOriginalConversation={isOriginalConversation}
            slaTimerDate={props.slaTimerDate}
          />
        </StyledCompactCellBase>
      );
    }
    return (
      <StyledCellBase
        $isCompact={isPreview || isTransparentPreview ? false : isCompact}
        $shouldShowQuickActionsOnCellHover={shouldShowQuickActionsOnCellHover}
        $shouldShowQuickActions={isSnoozeDropdownOpen}
        isSelected={isVisuallySelected}
        isPreview={props.isPreview}
        isTransparentPreview={props.isTransparentPreview}
        hasDoubleBottomRight={hasDoubleBottomRightIcons}
        hasDoubleTopLine
      >
        <ConversationCellContent
          isCompact={isPreview || isTransparentPreview ? false : isCompact}
          applicationChannelType={props.applicationChannelType}
          assignee={props.assignee}
          bumpActivity={props.bumpActivity}
          bumpComment={props.bumpComment}
          bumpTeammateSource={props.bumpTeammateSource}
          cellsContext={props.cellsContext}
          commentDraftSummary={props.commentDraftSummary}
          commentingTeammate={props.commentingTeammate}
          conversation={props.conversation}
          conversationHighlight={props.conversationHighlight}
          conversationSenders={props.conversationSenders}
          date={props.date}
          draft={props.draft}
          draftSeedAuthors={props.draftSeedAuthors}
          externalChannelType={props.externalChannelType}
          hasAutoShareDrafts={props.hasAutoShareDrafts}
          inbox={props.inbox}
          isSelected={props.isSelected}
          isSnoozeDropdownOpen={isSnoozeDropdownOpen}
          isStarred={props.isStarred}
          lastComment={props.lastComment}
          lastCommentDraft={props.lastCommentDraft}
          lastManualMessage={props.lastManualMessage}
          mentionableAliases={props.mentionableAliases}
          numMessages={props.numMessages}
          onOpenClick={onOpenClick}
          onSnoozeClick={onSnoozeClick}
          onSnoozeDropdownClose={onSnoozeDropdownClose}
          onToggleMarkAsRead={onToggleMarkAsRead}
          onTrashClick={onTrashClick}
          overlay={props.overlay}
          recipient={props.recipient}
          reminderDate={props.reminderDate}
          replyingTeammate={props.replyingTeammate}
          searchActivity={props.searchActivity}
          searchResultCommentSummary={props.searchResultCommentSummary}
          shouldConsiderInPrivateTag={props.shouldConsiderInPrivateTag}
          shouldRenderConversationSenders={props.shouldRenderConversationSenders}
          shouldShowQuickActionsOnCellHover={shouldShowQuickActionsOnCellHover}
          status={props.status}
          tags={props.tags}
          team={props.team}
          trackerStatus={props.trackerStatus}
          trackerTeammates={props.trackerTeammates}
          unreadStatus={props.unreadStatus}
          userId={props.userId}
          slaTimerDate={props.slaTimerDate}
          isOriginalConversation={isOriginalConversation}
        />
      </StyledCellBase>
    );
  };

  if (!props.onClick) {
    return renderCell();
  }

  // TODO: Use role=option with aria-selected instead of a button?
  const clickableCell = isUndefined(path) ? (
    <ChromelessButton
      ref={props.innerRef}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onMouseDown={onMouseDown}
      interactionId={interactionId}
    >
      {renderCell()}
    </ChromelessButton>
  ) : (
    <BlockLink
      ref={props.innerRef as any}
      to={path}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onMouseDown={onMouseDown}
      interactionId={interactionId}
    >
      {renderCell()}
    </BlockLink>
  );

  return (
    <LegacyInteractionParent interactionId={`conversation-cell-${id}`}>
      <ConversationCellDragSource
        cellsContext={props.cellsContext}
        sourceCustomViewId={props.sourceCustomViewId}
        conversation={props.conversation}
        customDragItem={props.customDragItem}
      >
        {clickableCell}
      </ConversationCellDragSource>
    </LegacyInteractionParent>
  );
};
